import type { ReactNode } from "react";
import { Pencil } from "lucide-react";
import type { BuyerLeadDetailDto } from "@/api/buyers";
import { Button, Timestamp } from "@/design-system/components/ui";
import { formatCurrency } from "@/design-system/lib/format";
import { DetailField, DetailFieldList, DetailSection, isWideFieldType } from "../detailBits";
import {
  fieldIsVisible,
  type BuyerFieldConfig,
  type BuyerSectionConfig,
} from "./buyerSectionConfig";

/**
 * One Overview card, rendered from the section config.
 *
 * <p>The pencil goes in <c>DetailSection</c>'s header-right slot — the shape
 * <c>QualificationsProfileTab</c>'s <c>ProfileSection</c> established: a
 * <c>Button variant="text" size="sm"</c> with a Pencil icon and an explicit
 * <c>aria-label</c>, because a bare icon in a header row is otherwise unnamed to
 * a screen reader and there are four of these on one page.</p>
 *
 * <p><b>No header row at all when the reader cannot write.</b> Not a disabled
 * pencil: a greyed control on somebody else's buyer invites a support ticket
 * asking why, and the answer — "it is not yours" — is already legible from the
 * Specialist field. This matches every other affordance on the page, all of
 * which hide rather than disable.</p>
 */
export function BuyerSectionCard({
  section,
  buyer,
  canEdit,
  onEdit,
  leading,
  trailing,
  "data-testid": testId,
}: {
  section: BuyerSectionConfig;
  buyer: BuyerLeadDetailDto;
  canEdit: boolean;
  onEdit: () => void;
  /** Read-only rows that belong to this card but are not patchable here — the
   *  assigned specialist, for instance, which moves through the assignment
   *  queue. Rendered ABOVE the configured fields. */
  leading?: ReactNode;
  /** Same, below. */
  trailing?: ReactNode;
  "data-testid"?: string;
}) {
  return (
    <DetailSection
      title={section.title}
      action={
        canEdit ? (
          <Button
            variant="text"
            size="sm"
            onClick={onEdit}
            aria-label={`Edit ${section.title}`}
            data-testid={`buyer-section-edit-${section.id}`}
          >
            <Pencil size={14} strokeWidth={1.5} aria-hidden /> Edit
          </Button>
        ) : null
      }
    >
      {/* One list per card, so the card's labels share a left edge and the
          track sizes to the widest label THIS card carries. */}
      <DetailFieldList data-testid={testId}>
        {leading}
        {/* A field whose `visibleWhen` the record does not meet is not a row
            at all — an individual's card carries no company rows. */}
        {section.fields.filter((field) => fieldIsVisible(field, buyer)).map((field) => (
          <DetailField
            key={field.key}
            label={field.label}
            value={readField(buyer, field)}
            wide={isWideFieldType(field)}
          />
        ))}
        {trailing}
      </DetailFieldList>
    </DetailSection>
  );
}

/**
 * One field, formatted for reading.
 *
 * <p>Returns <c>null</c> for anything absent so {@link DetailField} paints its em
 * dash — a field that vanishes when empty makes the reader wonder whether the
 * record has it at all, and "we do not know" is itself information on a CRM
 * record.</p>
 */
function readField(buyer: BuyerLeadDetailDto, field: BuyerFieldConfig): ReactNode {
  const value = buyer[field.key];
  if (value === null || value === undefined || value === "") return null;

  switch (field.type) {
    case "money":
      return typeof value === "number" ? formatCurrency(value) : String(value);
    case "date":
      return <Timestamp value={String(value)} />;
    case "yesno":
      // 🔴 Tri-state, and the absent case never renders as "No". "The buyer never
      // said" and "the buyer ruled SBA out" are different facts, and collapsing
      // them puts a decision in the buyer's mouth. Absence was already handled
      // above, so reaching here means an answer exists.
      return value === true ? "Yes" : "No";
    case "select":
      // The option's LABEL, never the stored token: "A company they own or
      // lead", not "CompanyOwnedOrLed". A value the options do not know (a
      // server member this client predates) prints as-is rather than vanishing.
      return field.options?.find((o) => o.value === String(value))?.label ?? String(value);
    default:
      return String(value);
  }
}
